import { useContext, useEffect, useState } from "react";
import {
  DOCUMENTS_STATUS,
  DOCUMENTS_STATUS_LABELS,
  documentsSections,
  FILE_TYPE_LABELS,
  fileTypeKeys,
  MANDATORY_DOCUMENTS,
  showDocumentsStar,
  statusColors,
} from "./constants";
import { UserContext } from "../../../Context";
import { USER_ROLES } from "../../../Constants/Strings";
import {
  uploadDocument,
  updateDocumentStatus,
  getUserDocuments,
  reviewUserById,
  getUserDetailsById,
} from "../../../Services/UserService";
import { useLocation, useParams } from "react-router-dom";
import { toProperCase } from "../../../Utils";
import toast from "react-hot-toast";
import { MESSAGES } from "../constant";
import { extractErrorMessage } from "../../../Utils/extractErrorMessage";
import { Loader } from "lucide-react";
import { Tooltip } from "../../../Components/Common";

const DocumentsTab = () => {
  const [documents, setDocuments] = useState({});
  const { user } = useContext(UserContext);
  const isEditable = [USER_ROLES.HR, USER_ROLES.ADMIN, USER_ROLES.DIRECTOR_MANAGEMENT].includes(user.role);
  const [loadingDocs, setLoadingDocs] = useState({});
  const [loadingUser, setLoadingUser] = useState(false);
  const { userId } = useParams();
  const location = useLocation();
  const isProfilePage = location.pathname.startsWith("/profile");
  const isViewMode = location.pathname.includes("view");
  const targetedUserId = isProfilePage ? user.id : userId;
  const canUpload = (!isEditable || isProfilePage) && !isViewMode;
  const canUpdateStatus = isEditable && !isProfilePage && !isViewMode;
  const [isOnboarded, setIsOnboarded] = useState(false);
  const [loadingOnboard, setLoadingOnboard] = useState(false);

  const fetchDocuments = async () => {
    try {
      const payload = targetedUserId;
      const res = await getUserDocuments({ user: payload, limit: 1000 });
      const docsMap = {};
      res.data.results.forEach((doc) => {
        const docName = Object.keys(fileTypeKeys).find(
          (key) => fileTypeKeys[key] === doc.file_type
        );
        if (docName) {
          docsMap[docName] = {
            id: doc.id,
            file: doc?.file ? { name: doc.file.split("/").pop(), url: doc.file }:null,
            status: doc.approval_status,
            is_onboard: doc.is_onboard,
            file_type: doc.file_type
          };
        }
      });
      setDocuments(docsMap);
    } catch (error) {
      const msg =
        extractErrorMessage(error) || MESSAGES.FAILED_TO_FETCH_DOCUMENTS;
      toast.error(msg);
    }
  };

  const fetchUser = async () => {
    if (!targetedUserId) {
      toast.error(MESSAGES.USER_ID_MISSING);
      return;
    }
    try {
      setLoadingUser(true);
      const res = await getUserDetailsById(targetedUserId);
      setIsOnboarded(res.data.is_onboard ?? false);
    } catch (error) {
      toast.error(extractErrorMessage(error) || MESSAGES.USER_DATA_ERROR);
    } finally {
      setLoadingUser(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, [targetedUserId]);

  useEffect(() => {
    fetchUser();
  }, [userId, isProfilePage]);

  const handleFileChange = async (docName, file) => {
    if (!file) return;
    setLoadingDocs((prev) => ({ ...prev, [docName]: true }));
    try {
      const fileTypeKey = fileTypeKeys[docName];
      if (!fileTypeKey) return;

      const targetUser =
            docName === FILE_TYPE_LABELS.APPOINTMENT_LETTER &&
            [USER_ROLES.HR, USER_ROLES.ADMIN, USER_ROLES.DIRECTOR_MANAGEMENT].includes(user.role)
              ? targetedUserId
              : user.id;
      const res = await uploadDocument(fileTypeKey, file, targetUser);
      if (res.status === 201 || res.status === 200) {
        setDocuments((prev) => ({
          ...prev,
          [docName]: {
            ...prev[docName],
            id: res.data.id,
            file: { name: file.name, url: res.data.file },
            status: DOCUMENTS_STATUS_LABELS.PENDING
          },
        }));
        toast.success(MESSAGES.DOCUMENT_UPLOAD_SUCCESS);
      } else {
        toast.error(
          extractErrorMessage(res) || MESSAGES.DOCUMENT_UPLOAD_FAILED
        );
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.DOCUMENT_UPLOAD_FAILED;
      toast.error(msg);
    } finally {
      setLoadingDocs((prev) => ({ ...prev, [docName]: false }));
    }
  };

  const handleStatusChange = async (docName, status) => {
      if (!canUpdateStatus) return;

      // Use existing document file_type, or fallback to your mapping
      const FILE_TYPE = documents[docName]?.file_type || fileTypeKeys[docName];

      if (!FILE_TYPE) {
        toast.error("Cannot determine file type for this document.");
        return;
      }
    const payload = {
      user: userId,
      file_type: FILE_TYPE,
      approval_status: status,
    };
    if (!canUpdateStatus) return;

    try {
      const res = await updateDocumentStatus(payload);
      if (res.status === 200) {
        setDocuments((prev) => ({
          ...prev,
          [docName]: {
            ...prev[docName],
            status: res.data.approval_status,
            id: res.data.id,
          },
        }));
        toast.success(
          `Status for ${docName} updated to ${toProperCase(status)}`
        );
      } else {
        toast.error(
          extractErrorMessage(res) || MESSAGES.DOCUMENT_STATUS_UPDATE_FAILED
        );
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || MESSAGES.DOCUMENT_STATUS_UPDATE_FAILED;
      toast.error(msg);
    }
  };

const canConfirmOnboarding = () => {
  for (const docName of MANDATORY_DOCUMENTS) {
    const doc = documents[docName];
    if (!doc?.file) return false; // file must be uploaded
    if (doc.status !== DOCUMENTS_STATUS_LABELS.APPROVED) return false; // status must be approved
  }

  for (const [docName, doc] of Object.entries(documents)) {
    if (MANDATORY_DOCUMENTS.includes(docName)) continue;
    if (
      doc?.status === DOCUMENTS_STATUS_LABELS.MISSING ||
      doc?.status === DOCUMENTS_STATUS_LABELS.REJECTED
    ) {
      return false;
    }
  }

  return true;
};


  const handleConfirmOnboarding = async () => {
    try {
      setLoadingOnboard(true);
      const payload = { id: targetedUserId, is_onboard: true };
      const response = await reviewUserById(payload);
      if (response.status === 200 || response.status === 204) {
        toast.success(MESSAGES.USER_ONBOARDING_CONFIRMED);
        setIsOnboarded(true);
      } else {
        toast.error(
          extractErrorMessage(response) ||
            MESSAGES.USER_ONBOARDING_CONFIRMATION_FAILED
        );
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) ||
        MESSAGES.USER_ONBOARDING_CONFIRMATION_FAILED;
      toast.error(msg);
    } finally {
      setLoadingOnboard(false);
    }
  };

  if (loadingUser)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={6} />
      </div>
    );

  const SmallLoader = () => (
    <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
  );

  const renderDocumentRow = (docName) => {
    const doc = documents[docName] || {};
    return (
      <>
        <td className={`px-1 py-1 border-b w-32 text-medium font-medium`}>
          {docName} {showDocumentsStar(docName)}
        </td>

        {canUpload && (
          <td className="border-b w-32">
            <div className="flex items-center justify-center h-full">
              {loadingDocs[docName] ? (
                <div className="flex items-center gap-2 text-blue-600">
                  <SmallLoader />
                  <span>Uploading...</span>
                </div>
              ) : (
                <Tooltip
                  text={
                    doc.status === DOCUMENTS_STATUS_LABELS.APPROVED
                      ? "File cannot be uploaded in approved state"
                      : isOnboarded
                      ? "Can not upload file, user is onboarded"
                      : docName === FILE_TYPE_LABELS.APPOINTMENT_LETTER
                      ? "Only HR can upload this file"
                      : "Upload File"
                  }
                >
                  <input
                    type="file"
                    className="text-xs h-9 w-28 rounded border cursor-pointer disabled:cursor-not-allowed disabled:opacity-80"
                    onChange={(e) =>
                      handleFileChange(docName, e.target.files[0])
                    }
                    disabled={
                      doc.status === DOCUMENTS_STATUS_LABELS.APPROVED ||
                      isOnboarded ||
                      docName === FILE_TYPE_LABELS.APPOINTMENT_LETTER
                    }
                  />
                </Tooltip>
              )}
            </div>
          </td>
        )}

        <td className="px-2 py-1 border-b max-w-[100px]">
          {loadingDocs[docName] ? (
            <div className="flex items-center justify-center h-full gap-2 text-blue-600">
              <SmallLoader />
              <span> Processing...</span>
            </div>
          ) : doc.file ? (
            <a
              href={doc.file.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 underline truncate block max-w-full text-center"
              title={doc.file.name}
            >
              {doc.file.name}
            </a>
          ) : (
            <div className="text-center">No file chosen</div>
          )}
        </td>

        <td className={`${canUpdateStatus ? "w-24" : "w-16"} border-b`}>
          <div className="text-left">
            {canUpdateStatus ? (
              <select
                value={doc.status || DOCUMENTS_STATUS_LABELS.PENDING}
                onChange={(e) => handleStatusChange(docName, e.target.value)}
                className="h-7 py-0.5 px-2 text-sm rounded-md border w-full text-left mr-1"
                disabled={isOnboarded}
                title={
                  isOnboarded ? "Can not change status, user is onboarded" : ""
                }
              >
                {DOCUMENTS_STATUS.map((item, idx) => (
                  <option key={idx} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </select>
            ) : (
              <span
                className={`px-2 py-1 rounded text-left flex justify-start text-medium font-medium mr-1 ${
                  statusColors[doc.status || DOCUMENTS_STATUS_LABELS.PENDING]
                }`}
              >
                {toProperCase(doc.status || DOCUMENTS_STATUS_LABELS.PENDING)}
              </span>
            )}
          </div>
        </td>
      </>
    );
  };

  return (
    <div className="md:px-6 md:py-2">
      {/* Mobile view */}
      <div className="md:hidden">
        {documentsSections.map((section) => (
          <div key={section.title} className="space-y-5">
            <h2 className="text-xl font-semibold text-primary border-b border-gray-300 pb-2 mt-4 md:mt-0">
              {section.title}
            </h2>
            {section.docs.map((doc) => (
              <div
                key={doc}
                className="bg-white p-4 rounded-lg shadow-sm flex flex-col gap-2"
              >
                <div className="font-semibold">
                  {toProperCase(doc)} {showDocumentsStar(doc)}
                </div>
                {loadingDocs[doc] ? (
                  <div className="flex items-center gap-2 text-blue-600 text-sm">
                    <SmallLoader />
                    <span>Uploading...</span>
                  </div>
                ) : (
                  canUpload && (
                    <input
                      type="file"
                      className="text-xs h-8 rounded border cursor-pointer disabled:cursor-not-allowed disabled:opacity-80"
                      onChange={(e) => handleFileChange(doc, e.target.files[0])}
                      disabled={
                        doc.status === DOCUMENTS_STATUS_LABELS.APPROVED ||
                        isOnboarded ||
                        doc === FILE_TYPE_LABELS.APPOINTMENT_LETTER
                      }
                      title={
                        doc.status === DOCUMENTS_STATUS_LABELS.APPROVED
                          ? "File cannot be uploaded in approved state"
                          : isOnboarded
                          ? "Can not upload file, user is onboarded"
                          : doc === "Appointment Letter"
                          ? "Only HR can upload this file"
                          : ""
                      }
                    />
                  )
                )}

                {/* Selected File */}
                <div className="truncate max-w-full">
                  {loadingDocs[doc] ? (
                    <div className="flex items-center gap-2 text-blue-600 text-sm">
                      <SmallLoader />
                      <span>Processing...</span>
                    </div>
                  ) : documents[doc]?.file ? (
                    <a
                      href={documents[doc].file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 underline truncate block max-w-full"
                      title={documents[doc].file.name}
                    >
                      {documents[doc].file.name}
                    </a>
                  ) : (
                    "No file chosen"
                  )}
                </div>

                <div>
                  {canUpdateStatus ? (
                    <select
                      value={
                        documents[doc]?.status ||
                        DOCUMENTS_STATUS_LABELS.PENDING
                      }
                      onChange={(e) => handleStatusChange(doc, e.target.value)}
                      className={`py-0.5 px-2 h-9 w-28 rounded-md border`}
                      disabled={isOnboarded}
                      title={
                        isOnboarded
                          ? "Can not upload file, user is onboarded"
                          : ""
                      }
                    >
                      {DOCUMENTS_STATUS.map((item, idx) => (
                        <option key={idx} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span
                      className={`px-2 py-1 rounded ${
                        statusColors[
                          documents[doc]?.status ||
                            DOCUMENTS_STATUS_LABELS.PENDING
                        ]
                      }`}
                    >
                      {toProperCase(
                        documents[doc]?.status ||
                          DOCUMENTS_STATUS_LABELS.PENDING
                      )}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Desktop view */}
      <div className="hidden md:flex md:gap-6">
        {documentsSections.map((section, idx) => (
          <div key={section.title} className="md:w-1/2">
            <h2 className="text-lg font-semibold text-primary border-b border-gray-300 pb-2">
              {section.title}
            </h2>
            <div className="overflow-x-hidden">
              <table className="min-w-full table-fixed text-xs border-collapse bg-white">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-1 border-b text-center">Document</th>
                    {canUpload && (
                      <th className="px-2 py-1 border-b text-center">Upload</th>
                    )}
                    <th className="px-2 py-1 border-b text-center">
                      Selected File
                    </th>
                    <th className="px-2 py-1 border-b text-center">Status</th>
                  </tr>
                </thead>
                <tbody className="overflow-x-hidden">
                  {section.docs.map((doc) => (
                    <tr
                      key={doc}
                      className="hover:bg-gray-50 transition-colors h-8"
                    >
                      {renderDocumentRow(doc)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-end gap-4 mb-6 md:mt-4 mt-6">
        {isEditable && !isProfilePage && !isOnboarded && (
          <div className="flex items-center gap-2 justify-end">
            <Tooltip
              text={
                documents[FILE_TYPE_LABELS.APPOINTMENT_LETTER]?.status ===
                DOCUMENTS_STATUS_LABELS.APPROVED
                  ? "Can not upload in approved state"
                  : "Upload File"
              }
            >
              <input
                type="file"
                id="appointmentLetterUpload"
                className="hidden text-white"
                onChange={(e) =>
                  handleFileChange("Appointment Letter", e.target.files[0])
                }
                disabled={
                  documents[FILE_TYPE_LABELS.APPOINTMENT_LETTER]?.status ===
                  DOCUMENTS_STATUS_LABELS.APPROVED
                }
              />
            <label
              htmlFor="appointmentLetterUpload"
              className={`px-2 py-1 bg-blue-500 text-white font-semibold rounded-md cursor-pointer flex items-center gap-2
                ${
                  documents[FILE_TYPE_LABELS.APPOINTMENT_LETTER]?.status ===
                  DOCUMENTS_STATUS_LABELS.APPROVED
                  ? "opacity-70 cursor-not-allowed"
                  : "hover:bg-blue-600"
                }`}
                >
              Upload Appointment Letter
            </label>
            </Tooltip>
          </div>
        )}

        {/* Onboard button */}
        {isEditable && !isProfilePage && !isOnboarded && (
          <div className="flex justify-end">
            <Tooltip
              text={
                !canConfirmOnboarding()
                  ? "Resume, Aadhaar Card, PAN Card, Appointment Letter and Offer Letter must be uploaded and approved. Other documents must not be missing or rejected."
                  : "Confirm Onboarding"
              }
            >
              <button
                className="px-2 py-1 bg-green-500 text-white rounded-md hover:bg-green-600 disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                onClick={handleConfirmOnboarding}
                disabled={loadingOnboard || !canConfirmOnboarding()}
              >
                {loadingOnboard ? (
                  <>
                    <SmallLoader />
                    Confirming...
                  </>
                ) : (
                  <span className="text-white font-semibold">
                    Confirm Onboarding
                  </span>
                )}
              </button>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentsTab;